import React, { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNotifications, Notification } from 'contexts/NotificationContext'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/Card'
import { PageHeader } from 'components/layout/PageHeader'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Badge } from 'components/ui/Badge'
import {
  Bell,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  Clock,
  Trash2,
  Eye,
  EyeOff,
  RefreshCw,
  CheckCheck,
  X,
  ExternalLink,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { cn, formatNotificationDateTime } from 'lib/utils'
import { BatchDetailsModal } from 'features/candidates/components/BatchDetailsModal'

type FilterType = 'all' | 'unread' | 'read'
type NotificationType = 'all' | 'system' | 'alert' | 'update' | 'reminder' | 'error'
type PriorityFilter = 'all' | 'low' | 'medium' | 'high' | 'urgent'
type FilterChip = { key: string; label: string; clear: () => void }
type StatusOption = { value: FilterType; label: string; count?: number }

export function NotificationsCenter() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const currentPath = `${location.pathname}${location.search}${location.hash}`
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    removeNotification,
    clearAll,
    refreshNotifications
  } = useNotifications()

  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<FilterType>('all')
  const [notificationType, setNotificationType] = useState<NotificationType>('all')
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null)
  const [showBatchDetails, setShowBatchDetails] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number | string>>(new Set())
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [showMobileFilters, setShowMobileFilters] = useState(false)

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set<string>()
    notifications.forEach(n => {
      if (n.category) cats.add(n.category)
    })
    return Array.from(cats).sort()
  }, [notifications])

  const activeFilters = useMemo<FilterChip[]>(() => {
    const chips: FilterChip[] = []
    if (filterType !== 'all') {
      const label =
        filterType === 'unread'
          ? t('notifications.center.filters.unreadOnly', 'Unread only')
          : t('notifications.center.filters.readOnly', 'Read only')
      chips.push({ key: 'status', label, clear: () => setFilterType('all') })
    }
    if (notificationType !== 'all') {
      chips.push({
        key: 'type',
        label: t(`notifications.types.${notificationType}`, notificationType),
        clear: () => setNotificationType('all')
      })
    }
    if (priorityFilter !== 'all') {
      chips.push({
        key: 'priority',
        label: t(`notifications.priority.${priorityFilter}`, priorityFilter),
        clear: () => setPriorityFilter('all')
      })
    }
    if (categoryFilter !== 'all') {
      chips.push({
        key: 'category',
        label: t(`notifications.categories.${categoryFilter}`, categoryFilter),
        clear: () => setCategoryFilter('all')
      })
    }
    return chips
  }, [filterType, notificationType, priorityFilter, categoryFilter, t])

  const hasActiveFilters = activeFilters.length > 0
  const statusOptions = useMemo<StatusOption[]>(() => ([
    { value: 'all', label: t('notifications.center.filters.all', 'All') },
    { value: 'unread', label: t('notifications.center.filters.unread', 'Unread'), count: unreadCount },
    { value: 'read', label: t('notifications.center.filters.read', 'Read') }
  ]), [t, unreadCount])

  const resetFilters = () => {
    setFilterType('all')
    setNotificationType('all')
    setPriorityFilter('all')
    setCategoryFilter('all')
  }

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 640) {
        setShowMobileFilters(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const renderFilterControls = (isMobile = false) => {
    const selectClass = cn(
      'px-3 py-2 border rounded-xl text-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow shadow-sm',
      isMobile ? 'w-full' : 'w-full'
    )
    const fieldLabelClass = 'text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400'

    return (
      <div className="flex flex-col gap-4">
        <div className={cn(
          'rounded-2xl border border-dashed border-gray-200 bg-gradient-to-r from-blue-50/70 via-white to-blue-50/70 p-3 dark:border-gray-800 dark:from-gray-800/70 dark:via-gray-900 dark:to-gray-800/70',
          'shadow-sm'
        )}>
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
            <Filter className="h-4 w-4 text-blue-500" />
            {t('notifications.center.quickFilter', 'Quick status')}
          </div>
          <div className="flex flex-wrap gap-2">
            {statusOptions.map(option => {
              const isActive = filterType === option.value
              return (
                <button
                  key={option.value}
                  onClick={() => setFilterType(option.value)}
                  aria-pressed={isActive}
                  className={cn(
                    'flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors',
                    isActive
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400/80 dark:bg-blue-900/30 dark:text-blue-200'
                      : 'border-transparent bg-white text-gray-600 hover:border-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-gray-700'
                  )}
                >
                  <span>{option.label}</span>
                  {option.count !== undefined && option.count > 0 && (
                    <span className="rounded-full bg-white px-2 text-xs font-semibold text-blue-600 dark:bg-blue-950 dark:text-blue-200">
                      {option.count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        <div className={cn(
          'grid gap-3',
          isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
        )}>
          <div className="flex flex-col gap-1">
            <span className={fieldLabelClass}>
              {t('notifications.center.filters.typeLabel', 'Notification type')}
            </span>
            <select
              value={notificationType}
              onChange={(e) => setNotificationType(e.target.value as NotificationType)}
              className={selectClass}
            >
              <option value="all">{t('notifications.center.filters.allTypes', 'All Types')}</option>
              <option value="system">{t('notifications.types.system', 'System')}</option>
              <option value="alert">{t('notifications.types.alert', 'Alert')}</option>
              <option value="update">{t('notifications.types.update', 'Update')}</option>
              <option value="reminder">{t('notifications.types.reminder', 'Reminder')}</option>
              <option value="error">{t('notifications.types.error', 'Error')}</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <span className={fieldLabelClass}>
              {t('notifications.center.filters.priorityLabel', 'Priority')}
            </span>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as PriorityFilter)}
              className={selectClass}
            >
              <option value="all">{t('notifications.center.filters.allPriorities', 'All Priorities')}</option>
              <option value="low">{t('notifications.priority.low', 'Low')}</option>
              <option value="medium">{t('notifications.priority.medium', 'Medium')}</option>
              <option value="high">{t('notifications.priority.high', 'High')}</option>
              <option value="urgent">{t('notifications.priority.urgent', 'Urgent')}</option>
            </select>
          </div>

          {categories.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className={fieldLabelClass}>
                {t('notifications.center.filters.categoryLabel', 'Category')}
              </span>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className={selectClass}
              >
                <option value="all">{t('notifications.center.filters.allCategories', 'All Categories')}</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {t(`notifications.categories.${cat}`, cat)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderNotificationActions = (notification: Notification) => (
    <>
      {notification.read ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            markAsUnread(notification.id)
          }}
          title={t('notifications.center.markUnread', 'Mark as unread')}
        >
          <EyeOff className="h-4 w-4" />
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            markAsRead(notification.id)
          }}
          title={t('notifications.center.markAsRead', 'Mark as read')}
        >
          <Eye className="h-4 w-4" />
        </Button>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation()
          removeNotification(notification.id)
        }}
        title={t('notifications.center.delete', 'Delete')}
      >
        <Trash2 className="h-4 w-4 text-red-500" />
      </Button>
    </>
  )

  // Filter and search notifications
  const filteredNotifications = useMemo(() => {
    return notifications.filter(notification => {
      // Filter by read status
      if (filterType === 'unread' && notification.read) return false
      if (filterType === 'read' && !notification.read) return false

      // Filter by notification type
      if (notificationType !== 'all' && notification.type !== notificationType) return false

      // Filter by priority
      if (priorityFilter !== 'all' && notification.priority !== priorityFilter) return false

      // Filter by category
      if (categoryFilter !== 'all' && notification.category !== categoryFilter) return false

      // Search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        const title = notification.title.toLowerCase()
        const message = notification.message.toLowerCase()
        const titleKey = notification.title_key || notification.titleKey || ''
        const messageKey = notification.message_key || notification.messageKey || ''
        
        if (
          !title.includes(query) &&
          !message.includes(query) &&
          !titleKey.toLowerCase().includes(query) &&
          !messageKey.toLowerCase().includes(query)
        ) {
          return false
        }
      }

      return true
    })
  }, [notifications, filterType, notificationType, priorityFilter, categoryFilter, searchQuery])

  // Group notifications by date or related object
  const groupedNotificationsData = useMemo(() => {
    const groups: Record<string, Notification[]> = {}
    const groupByRelated = filterType === 'all' // Only group when showing all
    
    filteredNotifications.forEach(notification => {
      let groupKey: string
      
      // Group by related object if available and grouping enabled
      if (groupByRelated && notification.related_object_type && notification.related_object_id) {
        groupKey = `${notification.related_object_type}_${notification.related_object_id}`
      } else {
        // Group by date
        const date = notification.timestamp || (notification.created_at ? new Date(notification.created_at) : new Date())
        const today = new Date()
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)
        
        if (date.toDateString() === today.toDateString()) {
          groupKey = t('notifications.center.today', 'Today')
        } else if (date.toDateString() === yesterday.toDateString()) {
          groupKey = t('notifications.center.yesterday', 'Yesterday')
        } else if (date.getTime() > today.getTime() - 7 * 24 * 60 * 60 * 1000) {
          groupKey = date.toLocaleDateString(undefined, { weekday: 'long' })
        } else {
          groupKey = date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
        }
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = []
      }
      groups[groupKey].push(notification)
    })
    
    return groups
  }, [filteredNotifications, t, filterType])

  // Use filtered notifications directly (pagination can be added later if needed)
  const displayNotifications = useMemo(() => {
    return Object.values(groupedNotificationsData).flat()
  }, [groupedNotificationsData])

  const totalPages = Math.ceil(displayNotifications.length / pageSize)
  const paginatedNotifications = useMemo(() => {
    const start = (page - 1) * pageSize
    const end = start + pageSize
    return displayNotifications.slice(start, end)
  }, [displayNotifications, page, pageSize])

  // Handle notification action
  const handleNotificationAction = (notification: Notification) => {
    if (notification.action_url) {
      if (notification.action_url.startsWith('http')) {
        window.open(notification.action_url, '_blank')
      } else {
        navigate(notification.action_url)
      }
    } else if (notification.action_type === 'view_candidate' && notification.related_object_id) {
      navigate(`/candidates/${notification.related_object_id}`, { state: { from: currentPath } })
    } else if (notification.action_type === 'view_offer' && notification.related_object_id) {
      navigate(`/offers/${notification.related_object_id}`)
    }
  }

  // Bulk operations
  const handleBulkDelete = () => {
    selectedIds.forEach(id => removeNotification(id))
    setSelectedIds(new Set())
  }

  const handleBulkMarkRead = () => {
    selectedIds.forEach(id => markAsRead(id))
    setSelectedIds(new Set())
  }

  const toggleSelect = (id: number | string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
  }

  const selectAll = () => {
    setSelectedIds(new Set(paginatedNotifications.map(n => n.id)))
  }

  const deselectAll = () => {
    setSelectedIds(new Set())
  }

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only handle if not typing in input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }

      if (e.key === 'n' || e.key === 'N') {
        // Open notifications (already on page, but could scroll to top)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } else if (e.key === 'm' || e.key === 'M') {
        // Mark all as read
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault()
          markAllAsRead()
        }
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [markAllAsRead])

  const getIcon = (type: string) => {
    switch (type) {
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />
      case 'alert':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />
      case 'update':
        return <CheckCircle className="h-5 w-5 text-blue-500" />
      case 'reminder':
        return <Clock className="h-5 w-5 text-orange-500" />
      default:
        return <Info className="h-5 w-5 text-gray-500" />
    }
  }

  const getPriorityBadge = (priority: string) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
      medium: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      urgent: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    }
    return (
      <Badge className={cn('text-xs', colors[priority as keyof typeof colors] || colors.medium)}>
        {t(`notifications.priority.${priority}`, priority)}
      </Badge>
    )
  }

  const getTranslatedTitle = (notification: Notification) => {
    if (notification.title_key || notification.titleKey) {
      return t(notification.title_key || notification.titleKey || '')
    }
    return notification.title
  }

  const getTranslatedMessage = (notification: Notification): string => {
    if (notification.message_key || notification.messageKey) {
      const messageKey = notification.message_key || notification.messageKey
      const messageParams = notification.message_params || notification.messageParams
      return t(messageKey || '', messageParams || {}) as string
    }
    return notification.message
  }

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification.id)
    }
    
    // If notification has batchId, show batch details modal
    const batchId = notification.batchId || notification.metadata?.batchId || notification.metadata?.batch_id
    if (batchId) {
      setSelectedNotification(notification)
      setShowBatchDetails(true)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <PageHeader
          title={t('notifications.center.title', 'Notifications Center')}
          className="mb-4"
          actions={
            <>
              <Button
                onClick={refreshNotifications}
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('common.refresh', 'Refresh')}
              </Button>
              {unreadCount > 0 && (
                <Button
                  onClick={markAllAsRead}
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                >
                  <CheckCheck className="h-4 w-4 mr-2" />
                  {t('notifications.markAllRead', 'Mark all read')}
                </Button>
              )}
            </>
          }
        />

        {/* Search and Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="space-y-4">
              {/* Search */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={t('notifications.center.searchPlaceholder', 'Search notifications...')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-full"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="sm:hidden w-full"
                  onClick={() => setShowMobileFilters(true)}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {t('notifications.center.filters.label', 'Filters')}
                  {hasActiveFilters && (
                    <span className="ml-2 rounded-full bg-blue-600 px-2 py-0.5 text-xs text-white">
                      {activeFilters.length}
                    </span>
                  )}
                </Button>
              </div>

              {/* Filters */}
              <div className="hidden sm:block">
                {renderFilterControls()}
              </div>

              {hasActiveFilters && (
                <div className="flex flex-wrap gap-2">
                  {activeFilters.map(chip => (
                    <button
                      key={chip.key}
                      onClick={chip.clear}
                      className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800/70"
                    >
                      {chip.label}
                      <X className="h-3 w-3" />
                    </button>
                  ))}
                  {activeFilters.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-3 text-xs"
                      onClick={resetFilters}
                    >
                      {t('notifications.center.clearFilters', 'Clear filters')}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mobile Filters */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 sm:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowMobileFilters(false)}
          />
          <div className="absolute inset-x-0 bottom-0 rounded-t-3xl bg-white p-4 shadow-2xl dark:bg-gray-900">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-base font-semibold">
                {t('notifications.center.filtersTitle', 'Filters')}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setShowMobileFilters(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="space-y-4">
              {renderFilterControls(true)}
              <div className="flex gap-2">
                <Button className="flex-1" onClick={() => setShowMobileFilters(false)}>
                  {t('common.apply', 'Apply')}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    resetFilters()
                    setShowMobileFilters(false)
                  }}
                >
                  {t('common.reset', 'Reset')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <CardContent className="p-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm font-medium">
                {selectedIds.size} {t('notifications.center.selected', 'selected')}
              </span>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <Button
                  onClick={handleBulkMarkRead}
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {t('notifications.center.markRead', 'Mark Read')}
                </Button>
                <Button
                  onClick={handleBulkDelete}
                  variant="outline"
                  size="sm"
                  className="w-full text-red-600 hover:text-red-700 sm:w-auto"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('notifications.center.delete', 'Delete')}
                </Button>
                <Button
                  onClick={deselectAll}
                  variant="ghost"
                  size="sm"
                  className="w-full sm:w-auto"
                >
                  {t('notifications.center.deselectAll', 'Deselect All')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notifications List */}
      {filteredNotifications.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                {searchQuery || filterType !== 'all' || notificationType !== 'all' || priorityFilter !== 'all' || categoryFilter !== 'all'
                  ? t('notifications.center.noResults', 'No notifications match your filters')
                  : t('notifications.noNotifications', 'No notifications')}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Select All / Deselect All */}
          {filteredNotifications.length > 0 && (
            <div className="flex w-full justify-center sm:justify-end">
              {selectedIds.size === 0 ? (
                <Button variant="outline" size="sm" onClick={selectAll} className="w-full sm:w-auto">
                  {t('notifications.center.selectAll', 'Select All')}
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={deselectAll} className="w-full sm:w-auto">
                  {t('notifications.center.deselectAll', 'Deselect All')}
                </Button>
              )}
            </div>
          )}
          
          {Object.entries(groupedNotificationsData).map(([groupKey, groupNotifications]) => {
            const isRelatedGroup = groupKey.includes('_') && !groupKey.includes(' ')
            const isExpanded = expandedGroups.has(groupKey) || !isRelatedGroup || groupNotifications.length <= 3
            
            // For related object groups, show summary if collapsed
            if (isRelatedGroup && !isExpanded && groupNotifications.length > 3) {
              return (
                <Card key={groupKey} className="cursor-pointer" onClick={() => {
                  const newExpanded = new Set(expandedGroups)
                  newExpanded.add(groupKey)
                  setExpandedGroups(newExpanded)
                }}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">
                          {groupNotifications.length} {t('notifications.center.relatedNotifications', 'related notifications')}
                        </p>
                        <p className="text-sm text-gray-500">{groupNotifications[0].title}</p>
                      </div>
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
              )
            }
            
            return (
              <div key={groupKey}>
                {!isRelatedGroup && (
                  <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3 sticky top-0 bg-white dark:bg-gray-900 py-2 z-10">
                    {groupKey}
                  </h2>
                )}
                {isRelatedGroup && groupNotifications.length > 3 && (
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                      {groupNotifications.length} {t('notifications.center.relatedNotifications', 'related notifications')}
                    </h2>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newExpanded = new Set(expandedGroups)
                        if (isExpanded) {
                          newExpanded.delete(groupKey)
                        } else {
                          newExpanded.add(groupKey)
                        }
                        setExpandedGroups(newExpanded)
                      }}
                    >
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                  </div>
                )}
                <div className="space-y-2">
                  {(isExpanded ? groupNotifications : groupNotifications.slice(0, 3)).map((notification) => (
                    <Card
                      key={notification.id}
                    className={cn(
                      'transition-all hover:shadow-md',
                      !notification.read && 'border-l-4 border-l-blue-500 bg-blue-50/50 dark:bg-blue-900/20',
                      selectedIds.has(notification.id) && 'ring-2 ring-blue-500'
                    )}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                          <div className="flex flex-1 items-start gap-3">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(notification.id)}
                              onChange={() => toggleSelect(notification.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="mt-1"
                            />
                            <div className="mt-1 shrink-0">
                              {getIcon(notification.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div 
                                className="cursor-pointer"
                                onClick={() => handleNotificationClick(notification)}
                              >
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                  <p className={cn(
                                    'text-sm font-medium',
                                    notification.read
                                      ? 'text-gray-700 dark:text-gray-300'
                                      : 'text-gray-900 dark:text-gray-100 font-semibold'
                                  )}>
                                    {getTranslatedTitle(notification)}
                                  </p>
                                  {getPriorityBadge(notification.priority)}
                                  {!notification.read && (
                                    <div className="w-2 h-2 bg-blue-500 rounded-full" />
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                  {getTranslatedMessage(notification)}
                                </p>
                                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                                  <span>
                                    {formatNotificationDateTime(
                                      notification.timestamp || notification.created_at,
                                      i18n.language
                                    )}
                                  </span>
                                  {notification.category && (
                                    <Badge variant="outline" className="text-xs">
                                      {t(`notifications.categories.${notification.category}`, notification.category)}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              
                              {/* Action Button */}
                              {notification.action_url || notification.action_type ? (
                                <div className="mt-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleNotificationAction(notification)
                                    }}
                                    className="text-xs"
                                  >
                                    {notification.action_label || t('notifications.center.viewDetails', 'View Details')}
                                    <ExternalLink className="h-3 w-3 ml-1" />
                                  </Button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <div className="hidden sm:flex items-center gap-1">
                            {renderNotificationActions(notification)}
                          </div>
                        </div>
                        <div className="flex items-center justify-end gap-1 sm:hidden">
                          {renderNotificationActions(notification)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  ))}
                </div>
              </div>
            )
          })}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {t('notifications.center.page', 'Page')} {page} {t('notifications.center.of', 'of')} {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  {t('common.previous', 'Previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  {t('common.next', 'Next')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Batch Details Modal */}
      {selectedNotification && (
        <BatchDetailsModal
          isOpen={showBatchDetails}
          onClose={() => {
            setShowBatchDetails(false)
            setSelectedNotification(null)
          }}
          notification={selectedNotification}
        />
      )}
    </div>
  )
}

